// Generic collection screen for cookbooks, mealplans, ingredients, utensils, units:
// search, sort by title, select mode with delete & share. (Replaces Swift's generic
// AccountObjectQueryView + AllIngredientView + AllUtensilView + AllIngredientUnitView.)
import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useNav, type DetailParams } from '../navigation';
import { useDiet, useHeader } from '../hooks';
import { dietsDeclared, type DietFlags } from '../logic/diets';
import { useAllIngredients, useAllUtensils, useLibrary } from '../store/library';
import { exportLibrary } from '../store/backup';
import { exportText } from '../platform/files';
import { copyText } from '../platform/clipboard';
import { normalize } from '../logic/text';
import { CardGrid, type CardData } from '../components/Cards';
import SelectionBar, { useSelection } from '../components/SelectionBar';
import { showMenu } from '../components/Dialogs';
import { Button, Chip, EmptyState, Field, IconButton, ListItem, Row, Screen, SectionHeader } from '../components/ui';
import type { IconName } from '../components/Icon';
import { useCreateMenu } from './LibraryScreen';
import { INGREDIENT_AISLES, UTENSIL_AISLES, type Diet } from '../types';
import { spacing, usePalette } from '../theme';

export type LibraryKind = 'cookbooks' | 'mealplans' | 'ingredients' | 'utensils' | 'units';

const META: Record<LibraryKind, { icon: IconName; route: 'Cookbook' | 'Mealplan' | 'Ingredient' | 'Utensil' | 'Unit'; title: string }> = {
  cookbooks: { icon: 'cookbook', route: 'Cookbook', title: 'kinds.cookbooks' },
  mealplans: { icon: 'mealplan', route: 'Mealplan', title: 'kinds.mealplans' },
  ingredients: { icon: 'ingredient', route: 'Ingredient', title: 'kinds.ingredients' },
  utensils: { icon: 'utensil', route: 'Utensil', title: 'kinds.utensils' },
  units: { icon: 'unit', route: 'Unit', title: 'kinds.units' },
};

type Item = { id: string; title: string; subtitle?: string; aisle?: string; builtIn?: boolean; image?: CardData['media']; diets?: Diet[]; flags?: DietFlags; unchecked?: boolean };

export default function EntityListScreen({ route }: NativeStackScreenProps<DetailParams, 'EntityList'>) {
  // Old links may name a kind that no longer exists (techniques): show cookbooks instead.
  const kind: LibraryKind = route.params?.kind && route.params.kind in META ? route.params.kind : 'cookbooks';
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const meta = META[kind];
  const lib = useLibrary();
  const ingredients = useAllIngredients();
  const utensils = useAllUtensils();
  const [query, setQuery] = useState('');
  const sel = useSelection();
  const openCreate = useCreateMenu();
  const diet = useDiet();

  const items: Item[] = useMemo(() => {
    switch (kind) {
      case 'cookbooks':
        return Object.values(lib.cookbooks).map(x => ({ id: x.id, title: x.title, image: x.image, subtitle: `${x.sections.reduce((n, sec) => n + sec.recipeIds.length, 0)} ${t('kinds.recipes')}` }));
      case 'mealplans':
        return Object.values(lib.mealplans).map(x => ({ id: x.id, title: x.title, subtitle: t('mealplan.days', { count: x.cycleDays }) }));
      case 'ingredients':
        return Object.values(ingredients)
          .filter(x => !diet.ingredientHidden(x))
          .map(x => ({ id: x.id, title: x.title, aisle: x.aisle, builtIn: x.builtIn, diets: diet.ingredientConflicts(x), unchecked: !dietsDeclared(x) }));
      case 'utensils':
        return Object.values(utensils).map(x => ({ id: x.id, title: x.title, aisle: x.aisle, builtIn: x.builtIn }));
      case 'units':
        return Object.values(lib.units).map(x => ({ id: x.id, title: x.title, subtitle: [x.symbol, ...x.searchTerms].filter(Boolean).join(', ') }));
    }
  }, [kind, lib, ingredients, utensils, diet, t]);

  const shown = useMemo(() => {
    const q = normalize(query);
    return items.filter(i => !q || normalize(i.title).includes(q)).sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
  }, [items, query]);

  const open = (id: string) => (sel.editing ? sel.toggle(id) : nav.navigate(meta.route, { id }));
  const longPress = (id: string) =>
    showMenu([
      { label: t('common.open'), icon: 'forward', onPress: () => nav.navigate(meta.route, { id }) },
      { label: t('common.copyTitle'), icon: 'copy', onPress: () => copyText(items.find(i => i.id === id)?.title ?? '') },
      { label: t('common.share'), icon: 'share', onPress: () => exportText(`${kind}.json`, exportLibrary({ [kind]: [id] })) },
    ]);

  useHeader(
    {
      title: t(meta.title as 'kinds.recipes'),
      headerRight: () =>
        sel.editing ? (
          <Row gap={spacing.m}>
            <IconButton icon="checklist" label={t('common.selectAll')} onPress={() => sel.setAll(shown.map(i => i.id))} />
            <IconButton icon="check" label={t('common.done')} onPress={sel.stop} />
          </Row>
        ) : (
          <Row gap={spacing.m}>
            <IconButton icon="plus" label={t('library.create')} onPress={() => openCreate(kind)} />
            <IconButton
              icon="more"
              label={t('common.more')}
              onPress={() =>
                showMenu([
                  { label: t('library.filterAll'), icon: 'check', onPress: () => {} },
                  { label: t('library.filterFavourites'), icon: 'sparkles', disabled: true, onPress: () => {} },
                  { label: t('common.select'), icon: 'checklist', onPress: sel.start },
                ])
              }
            />
          </Row>
        ),
    },
    [sel.editing, shown, kind, t],
  );

  const grouped = kind === 'ingredients' || kind === 'utensils';
  const aisles: readonly string[] = kind === 'ingredients' ? INGREDIENT_AISLES : UTENSIL_AISLES;

  return (
    <View style={{ flex: 1 }}>
      <Screen>
        <Field placeholder={t('common.search')} value={query} onChangeText={setQuery} autoCorrect={false} accessibilityLabel={t('common.search')} />
        {shown.length === 0 ? (
          <EmptyState icon={meta.icon} title={query ? t('library.noResults') : t('common.empty')} action={!query ? <Button label={t('library.create')} icon="plus" onPress={() => openCreate(kind)} /> : undefined} />
        ) : grouped ? (
          aisles.map(aisle => {
            const inAisle = shown.filter(i => i.aisle === aisle);
            if (!inAisle.length) return null;
            return (
              <View key={aisle} style={{ gap: spacing.s }}>
                <SectionHeader title={t(`aisles.${aisle}` as 'aisles.other')} color={aisle === 'other' ? p.warning : undefined} />
                <Row>
                  {inAisle.map(i => (
                    <Chip
                      key={i.id}
                      label={i.title}
                      icon={sel.editing ? (sel.has(i.id) ? 'checkCircle' : 'circle') : i.unchecked ? 'warning' : i.diets?.length ? 'close' : meta.icon}
                      color={!sel.editing && (i.unchecked || i.diets?.length) ? (i.diets?.length ? p.danger : p.warning) : undefined}
                      selected={sel.editing ? sel.has(i.id) : undefined}
                      onPress={() => open(i.id)}
                    />
                  ))}
                </Row>
              </View>
            );
          })
        ) : kind === 'units' ? (
          shown.map(i => <ListItem key={i.id} title={i.title} subtitle={i.subtitle} icon="unit" selected={sel.editing ? sel.has(i.id) : undefined} onPress={() => open(i.id)} onLongPress={() => longPress(i.id)} />)
        ) : (
          <CardGrid
            items={shown.map(i => ({ id: i.id, title: i.title, subtitle: i.subtitle, media: i.image, icon: meta.icon, diets: i.flags }))}
            onPress={open}
            onLongPress={longPress}
            isSelected={sel.editing ? id => sel.has(id) : undefined}
          />
        )}
      </Screen>
      {sel.editing ? (
        <SelectionBar
          count={sel.ids.size}
          onDelete={() => {
            lib.remove(kind, [...sel.ids]);
            sel.stop();
          }}
          extra={[{ label: t('common.share'), icon: 'share', onPress: () => exportText(`${kind}.json`, exportLibrary({ [kind]: [...sel.ids] })) }]}
        />
      ) : null}
    </View>
  );
}
